
import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle, 
  CardFooter
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Copy } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Form, FormField, FormItem, FormLabel, FormControl, FormDescription } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { usePaymentSettings, DEFAULT_PAYMENT_SETTINGS, DEFAULT_PAYMENT_RECEIVED_TEMPLATE, DEFAULT_ESTIMATE_TEMPLATE } from "@/hooks/usePaymentSettings";
import SmsDisableToggleCard from "@/components/profile/SmsDisableToggleCard";

const receiptCustomizationSchema = z.object({
  includeLogo: z.boolean().default(true),
  includeAddress: z.boolean().default(true),
  includeContactInfo: z.boolean().default(true),
  includeTaxId: z.boolean().default(false),
  includeThankYouMessage: z.boolean().default(true),
  customMessage: z.string().optional(),
  taxId: z.string().optional(),
  paymentRequestTemplate: z.string().optional(),
  paymentReminderTemplate: z.string().optional(),
  paymentReceivedTemplate: z.string().optional(),
  estimateTemplate: z.string().optional(),
  reminderIntervalDays: z.coerce.number().int().min(0).max(365).default(0),
  paymentDueDays: z.coerce.number().int().min(0).max(365).default(14),
});

type ReceiptCustomizationValues = z.infer<typeof receiptCustomizationSchema>;

const PaymentSettings = () => {
  const { settings, save } = usePaymentSettings();
  const receiptForm = useForm<ReceiptCustomizationValues>({
    resolver: zodResolver(receiptCustomizationSchema),
    defaultValues: settings,
  });

  useEffect(() => {
    receiptForm.reset(settings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    settings.paymentRequestTemplate,
    settings.paymentReminderTemplate,
    settings.paymentReceivedTemplate,
    settings.estimateTemplate,
    settings.reminderIntervalDays,
    settings.paymentDueDays,
    settings.includeLogo,
    settings.includeAddress,
    settings.includeContactInfo,
    settings.includeTaxId,
    settings.taxId,
    settings.includeThankYouMessage,
    settings.customMessage,
  ]);

  const watched = receiptForm.watch();
  const includeLogo = watched.includeLogo;
  const includeAddress = watched.includeAddress;

  const includeContactInfo = watched.includeContactInfo;
  const includeTaxId = watched.includeTaxId;

  const togglesChanged =
    watched.includeLogo !== settings.includeLogo ||
    watched.includeAddress !== settings.includeAddress ||
    watched.includeContactInfo !== settings.includeContactInfo ||
    watched.includeTaxId !== settings.includeTaxId ||
    (watched.taxId ?? "") !== (settings.taxId ?? "");

  const scheduleChanged =
    Number(watched.reminderIntervalDays ?? 0) !== Number(settings.reminderIntervalDays ?? 0) ||
    Number(watched.paymentDueDays ?? 0) !== Number(settings.paymentDueDays ?? 0);

  const templatesChanged =
    (watched.paymentRequestTemplate ?? "") !== (settings.paymentRequestTemplate ?? "") ||
    (watched.paymentReminderTemplate ?? "") !== (settings.paymentReminderTemplate ?? "") ||
    (watched.paymentReceivedTemplate ?? "") !== (settings.paymentReceivedTemplate ?? "") ||
    (watched.estimateTemplate ?? "") !== (settings.estimateTemplate ?? "") ||
    watched.includeThankYouMessage !== settings.includeThankYouMessage ||
    (watched.customMessage ?? "") !== (settings.customMessage ?? "");

  const handleSaveToggles = async () => {
    const data = receiptForm.getValues();
    try {
      await save({
        ...settings,
        includeLogo: data.includeLogo,
        includeAddress: data.includeAddress,
        includeContactInfo: data.includeContactInfo,
        includeTaxId: data.includeTaxId,
        taxId: data.taxId ?? settings.taxId,
      });
      toast.success("Settings saved");
    } catch {
      toast.error("Failed to save settings");
    }
  };

  const handleSaveSchedule = async () => {
    const data = receiptForm.getValues();
    try {
      await save({
        ...settings,
        reminderIntervalDays: Number(data.reminderIntervalDays ?? settings.reminderIntervalDays),
        paymentDueDays: Number(data.paymentDueDays ?? settings.paymentDueDays),
      });
      toast.success("Payment schedule saved");
    } catch {
      toast.error("Failed to save schedule");
    }
  };

  const handleSaveTemplates = async () => {
    const data = receiptForm.getValues();
    try {
      await save({
        ...settings,
        paymentRequestTemplate: data.paymentRequestTemplate ?? settings.paymentRequestTemplate,
        paymentReminderTemplate: data.paymentReminderTemplate ?? settings.paymentReminderTemplate,
        paymentReceivedTemplate: data.paymentReceivedTemplate ?? settings.paymentReceivedTemplate,
        estimateTemplate: data.estimateTemplate ?? settings.estimateTemplate,
        includeThankYouMessage: data.includeThankYouMessage,
        customMessage: data.customMessage ?? settings.customMessage,
      });
      toast.success("Templates saved");
    } catch {
      toast.error("Failed to save templates");
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight">Payment Settings</h1>
        <p className="text-muted-foreground mt-2">
          Configure how your payment invoices, receipts and estimates will look and what information to include
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>PDF Customization</CardTitle>
          <CardDescription>
            Configure how your payment invoices, receipts and estimates will look and what information to include
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...receiptForm}>
            <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <FormField
                  control={receiptForm.control}
                  name="includeLogo"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Company Logo</FormLabel>
                        <FormDescription>
                          Include your company logo on receipts
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={receiptForm.control}
                  name="includeAddress"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Company Address</FormLabel>
                        <FormDescription>
                          Include your business address on receipts
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={receiptForm.control}
                  name="includeContactInfo"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Contact Information</FormLabel>
                        <FormDescription>
                          Include your contact details on receipts
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={receiptForm.control}
                  name="includeTaxId"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Tax ID / VAT</FormLabel>
                        <FormDescription>
                          Include your tax identification number
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              
              {includeTaxId && (
                <div className="p-4 rounded-lg border bg-muted/20">
                  <FormField
                    control={receiptForm.control}
                    name="taxId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tax ID / VAT Number</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              )}
              <div className="flex justify-end">
                <Button type="button" onClick={handleSaveToggles} className="w-36" disabled={!togglesChanged}>
                  Save Settings
                </Button>
              </div>

              <SmsDisableToggleCard />

              <div className="rounded-lg border p-4 space-y-4">
                <div>
                  <h3 className="text-base font-semibold">Payment Schedule</h3>
                  <p className="text-sm text-muted-foreground">Set how often reminders are sent and when payments are due.</p>
                  <p className="text-xs text-muted-foreground mt-1">Note: setting "Payment due in" to <strong>0</strong> means the payment is due immediately (the message will read "Payment due: immediately").</p>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={receiptForm.control}
                    name="reminderIntervalDays"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Send payment reminder every (days)</FormLabel>
                        <FormControl>
                          <Input type="number" min={0} max={365} {...field} value={field.value ?? 0} />
                        </FormControl>
                        <FormDescription>A reminder will be sent at this interval until paid. Set to 0 to disable.</FormDescription>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={receiptForm.control}
                    name="paymentDueDays"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Payment due in (days)</FormLabel>
                        <FormControl>
                          <Input type="number" min={0} max={365} {...field} value={field.value ?? 14} />
                        </FormControl>
                        <FormDescription>Default due date offset from when a request is sent. 0 = immediately.</FormDescription>
                      </FormItem>
                    )}
                  />
                </div>
                <div className="flex justify-end">
                  <Button type="button" onClick={handleSaveSchedule} className="w-36" disabled={!scheduleChanged}>
                    Save Schedule
                  </Button>
                </div>
              </div>

              <div className="bg-muted/40 rounded-md p-4">
                <p className="text-sm font-medium mb-1">Available tags (for all templates below)</p>
                <p className="text-xs text-muted-foreground mb-3">Tap a tag to copy it, then paste into any template.</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    "{{customerName}}",
                    "{{businessName}}",
                    "{{amount}}",
                    "{{description}}",
                    "{{date}}",
                    "{{dueDate}}",
                    "{{accountName}}",
                    "{{accountNumber}}",
                    "{{sortCode}}",
                  ].map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(tag);
                          toast.success(`Copied ${tag}`);
                        } catch {
                          toast.error("Failed to copy");
                        }
                      }}
                      className="inline-flex items-center gap-1.5 rounded-md border bg-background px-2.5 py-1.5 text-xs font-mono hover:bg-accent hover:text-accent-foreground transition-colors active:scale-95"
                    >
                      <code>{tag}</code>
                      <Copy className="h-3 w-3 opacity-60" />
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border p-4 space-y-4">
                <div>
                  <h3 className="text-base font-semibold">Invoice Template</h3>
                </div>

                <FormField
                  control={receiptForm.control}
                  name="paymentRequestTemplate"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Textarea rows={8} {...field} value={field.value as string ?? ""} placeholder="Hello {{customerName}},..." />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <div className="rounded-lg border p-4 space-y-4">
                <div>
                  <h3 className="text-base font-semibold">Payment Reminder Template</h3>
                </div>

                <FormField
                  control={receiptForm.control}
                  name="paymentReminderTemplate"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Textarea rows={8} {...field} value={field.value as string ?? ""} placeholder="Hello {{customerName}}, this is a friendly reminder..." />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <div className="rounded-lg border p-4 space-y-4">
                <div>
                  <h3 className="text-base font-semibold">Payment Received Template</h3>
                </div>

                <FormField
                  control={receiptForm.control}
                  name="paymentReceivedTemplate"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Textarea rows={8} {...field} value={field.value as string ?? ""} placeholder="Hello {{customerName}}, thank you! We've received your payment..." />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <div className="rounded-lg border p-4 space-y-4">
                <div>
                  <h3 className="text-base font-semibold">Estimate Template</h3>
                </div>

                <FormField
                  control={receiptForm.control}
                  name="estimateTemplate"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Textarea rows={8} {...field} value={field.value as string ?? ""} placeholder="Hello {{customerName}}, please find our estimate below..." />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex justify-end items-center mt-6">
                <Button type="button" onClick={handleSaveTemplates} className="w-36" disabled={!templatesChanged}>
                  Save Templates
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSettings;
